import type {
  ActiveUpsellDiscountType,
  UpsellProductItem,
} from "@intelligems/ig-types/dtos/upsell-product-rules";
import type { BundleDiscountTier } from "@intelligems/ig-types/dtos/bundle-discount";
import type {
  DiscountSignatureMessageFields,
  DiscountSigningKeys,
  ParsedDiscountSignature,
  SignDiscountOfferArgs,
  VerifiedDiscount,
  VerifyDiscountSignatureArgs,
} from "@intelligems/ig-types/dtos/discount-signing";

import {
  timingSafeEqualHex,
  upsellMacHex,
  upsellVariantGid,
} from "./upsellSignature";

/**
 * Value + expiry bound signing for upsell discount offers ("v2").
 *
 * The legacy MAC (see `upsellSignature.ts`) only covers the variant and the block, so a
 * harvested signature stays valid forever and the discount amount itself is
 * unauthenticated — the function reads it from the block metafield. v2 pulls the discount
 * type, the discount amount and an expiry date into the signed message, so the function
 * can apply the value it verified instead of a value the merchant can change underneath
 * an old signature, and a harvested signature dies on a known day.
 *
 * The tag stays SipHash-2-4 for the reasons in `upsellSignature.ts`: the verifier runs in
 * a WASM sandbox with no crypto APIs and a hard per-invocation instruction budget.
 *
 * ## Wire format
 *
 * The `_igUpsellSig` cart attribute carries six pipe-delimited fields (no field may
 * contain `|`):
 *
 * ```text
 * v2|<kid>|<exp>|<dT>|<dV>|<tag>
 * ```
 *
 * - `v2`  — literal version prefix. Anything else is not a v2 signature.
 * - `kid` — signing key id, `^[a-z0-9-]{1,16}$`. Verifiers hold a map of every key id
 *           they accept, so a key can be rotated by adding the new id to the map,
 *           switching minting over to it, and dropping the old id one expiry window
 *           later — no flag day, and no in-flight signature is killed.
 * - `exp` — last valid date as `YYYY-MM-DD`, `^[0-9]{4}-[0-9]{2}-[0-9]{2}$`. The only
 *           clock the function has is the store-local date, so expiry is day granular and
 *           store local: valid while `exp >= currentDate` (ISO dates compare
 *           lexicographically).
 * - `dT`  — discount type: `p` (percentage) or `f` (fixed).
 * - `dV`  — the discount tiers: a bare positive decimal (`15`) for one tier from one
 *           product, or ascending `minProducts:value` pairs (`3:30,4:40`).
 * - `tag` — `^[0-9a-f]{16}$`, SipHash-2-4 under `keys[kid]` (the same 16-byte key
 *           derivation the legacy signer uses) over the UTF-8 message:
 *
 * ```text
 * v2|<kid>|<exp>|<dT>|<dV>|<variantGid>|<blockId>
 * ```
 *
 * The variant gid and the block id are covered by the MAC but are NOT on the wire: the
 * verifier takes them from the cart line itself (merchandise id and the `_igUpsell`
 * attribute), so a signature can never be moved onto another variant or another block,
 * and those two bindings cost zero payload bytes. Every other field is verified from the
 * literal substring received, so signer and verifier can never disagree on canonical form.
 *
 * ## Fail-closed contract
 *
 * `parseDiscountSignature` returns null rather than throwing for every malformed input,
 * and `verifyDiscountSignature` returns null for a missing payload, a malformed payload,
 * an unknown key id, a tag mismatch or an expired signature. A caller that gets null must
 * apply no discount. Minting is the opposite — `signDiscountOffer` throws on bad input,
 * because a malformed signature would silently fail to verify at checkout.
 *
 * One implementation, shared by the backend signer and the in-function verifier, so they
 * can never disagree on a tag.
 */
export const DISCOUNT_SIGNATURE_VERSION = "v2";

const DISCOUNT_SIGNATURE_FIELD_COUNT = 6;

const KID_PATTERN = /^[a-z0-9-]{1,16}$/;
const DATE_PATTERN = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const DISCOUNT_VALUE_PATTERN = /^[0-9]+(\.[0-9]+)?$/;
const DISCOUNT_TIERS_PATTERN =
  /^[0-9]+:[0-9]+(\.[0-9]+)?(,[0-9]+:[0-9]+(\.[0-9]+)?)*$/;
const TAG_PATTERN = /^[0-9a-f]{16}$/;

/** The tiers a surface with no threshold to reach authors. */
export function toSingleTier(value: number): BundleDiscountTier[] {
  return [{ minimumProducts: 1, value }];
}

// The inverse of `toSingleTier`, for the `discountValue` fields that cannot carry
// tiers. A threshold is as unrepresentable as a second tier, so both answer null
// rather than a value that would read as unconditional. Mirrors the bare-encoding
// condition in `serializeDiscountTiers`.
export function toDiscountValue(
  tiers: readonly BundleDiscountTier[]
): number | null {
  return tiers.length === 1 && tiers[0].minimumProducts === 1
    ? tiers[0].value
    : null;
}

// Thresholds, not increments: a 30% and a 40% tier means 40%, never 70%. Input must ascend —
// `parseDiscountTiers` guarantees it, and `resolveBundleTier` sorts merchant config first.
export function determineQualifyingTier<T extends { minimumProducts: number }>(
  count: number,
  ascendingTiers: readonly T[]
): T | undefined {
  let qualifying: T | undefined;
  for (const tier of ascendingTiers) {
    if (tier.minimumProducts > count) {
      break;
    }
    qualifying = tier;
  }
  return qualifying;
}

// Ascending order is the wire format's concern, not the caller's — nothing validates
// authored order. A lone tier from one product keeps the bare single-value encoding.
function serializeDiscountTiers(tiers: BundleDiscountTier[]): string {
  const ascending = [...tiers].sort(
    (a, b) => a.minimumProducts - b.minimumProducts
  );
  if (ascending.length === 1 && ascending[0].minimumProducts === 1) {
    return String(ascending[0].value);
  }
  return ascending
    .map((tier) => `${String(tier.minimumProducts)}:${String(tier.value)}`)
    .join(",");
}

function parseDiscountTiers(dV: string): BundleDiscountTier[] | null {
  if (DISCOUNT_VALUE_PATTERN.test(dV)) {
    const value = parseFloat(dV);
    return value > 0 ? toSingleTier(value) : null;
  }
  if (!DISCOUNT_TIERS_PATTERN.test(dV)) {
    return null;
  }

  const tiers: BundleDiscountTier[] = [];
  for (const pair of dV.split(",")) {
    const [threshold, value] = pair.split(":");
    const minimumProducts = parseInt(threshold, 10);
    const tier = { minimumProducts, value: parseFloat(value) };
    if (minimumProducts < 1 || !(tier.value > 0)) {
      return null;
    }
    if (
      tiers.length > 0 &&
      minimumProducts <= tiers[tiers.length - 1].minimumProducts
    ) {
      return null;
    }
    tiers.push(tier);
  }
  return tiers;
}

/**
 * Look a key id up as an OWN property of the key map, never through the prototype chain.
 *
 * The key map is `JSON.parse` output, so it carries `Object.prototype`, and the key id is
 * a buyer-settable cart attribute. The id grammar (`^[a-z0-9-]{1,16}$`) is lower-case only,
 * which happens to exclude `toString` and `__proto__` — but not `constructor`. A plain
 * index would hand that back as the "secret", pass the truthiness guard, and then throw
 * inside the MAC, taking down the whole discount run on unauthenticated input instead of
 * rejecting it. Guarding the lookup rather than the grammar keeps the rejection correct if
 * the grammar ever widens, and matches the Rust port, whose `HashMap` has no prototype to
 * walk.
 *
 * `Object.prototype.hasOwnProperty.call` rather than `Object.hasOwn`: the verifier runs on
 * Javy/QuickJS, where ES2022 built-ins cannot be assumed.
 */
function ownKey(keys: Record<string, string>, kid: string): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(keys, kid)) {
    return undefined;
  }
  const secret = keys[kid];
  return typeof secret === "string" ? secret : undefined;
}

function discountTypeCode(discountType: ActiveUpsellDiscountType): string {
  return discountType === "percentage" ? "p" : "f";
}

function discountTypeFromCode(code: string): ActiveUpsellDiscountType | null {
  if (code === "p") {
    return "percentage";
  }
  if (code === "f") {
    return "fixed";
  }
  return null;
}

/**
 * The signed message binds the offer to the variant and the block (as the legacy MAC did)
 * AND to the key id, expiry and discount, so none of them can be swapped after the fact.
 */
export function buildDiscountSignatureMessage(
  fields: DiscountSignatureMessageFields,
  variantGid: string,
  blockId: string
): string {
  return `${DISCOUNT_SIGNATURE_VERSION}|${fields.kid}|${fields.exp}|${fields.dT}|${fields.dV}|${variantGid}|${blockId}`;
}

/**
 * Sign an upsell offer with the active key. Returns the full attribute payload.
 *
 * Throws on anything that could not verify later: an active key id that is absent or
 * malformed, an expiry that is not `YYYY-MM-DD`, or tiers that would not parse back
 * (empty, a non-positive or exponent-formatted value, thresholds that do not ascend).
 */
export function signDiscountOffer(
  keys: DiscountSigningKeys,
  args: SignDiscountOfferArgs
): string {
  const kid = keys.active;
  if (!KID_PATTERN.test(kid)) {
    throw new Error(`Invalid upsell signing key id: "${kid}"`);
  }
  const secret = ownKey(keys.keys, kid);
  if (!secret) {
    throw new Error(`No upsell signing key for active key id: "${kid}"`);
  }
  if (!DATE_PATTERN.test(args.expiryDate)) {
    throw new Error(
      `Invalid upsell signature expiry date: "${args.expiryDate}"`
    );
  }
  const dV = serializeDiscountTiers(args.discountTiers);
  if (!parseDiscountTiers(dV)) {
    throw new Error(`Invalid upsell discount tiers: "${dV}"`);
  }

  const fields: DiscountSignatureMessageFields = {
    kid,
    exp: args.expiryDate,
    dT: discountTypeCode(args.discountType),
    dV,
  };
  const tag = upsellMacHex(
    secret,
    buildDiscountSignatureMessage(
      fields,
      upsellVariantGid(args.variantId),
      args.blockId
    )
  );

  return `${DISCOUNT_SIGNATURE_VERSION}|${fields.kid}|${fields.exp}|${fields.dT}|${fields.dV}|${tag}`;
}

/**
 * Parse a v2 attribute payload into its fields. Returns null — never throws — for any
 * payload that is not exactly six well-formed v2 fields, including a legacy bare-hex
 * signature.
 */
export function parseDiscountSignature(
  payload: string
): ParsedDiscountSignature | null {
  const parts = payload.split("|");
  if (parts.length !== DISCOUNT_SIGNATURE_FIELD_COUNT) {
    return null;
  }

  const [version, kid, exp, dT, dV, tag] = parts;
  if (version !== DISCOUNT_SIGNATURE_VERSION) {
    return null;
  }
  if (!KID_PATTERN.test(kid)) {
    return null;
  }
  if (!DATE_PATTERN.test(exp)) {
    return null;
  }
  if (!TAG_PATTERN.test(tag)) {
    return null;
  }

  const discountType = discountTypeFromCode(dT);
  if (!discountType) {
    return null;
  }

  const discountTiers = parseDiscountTiers(dV);
  if (!discountTiers) {
    return null;
  }

  return { kid, exp, dT, dV, tag, discountType, discountTiers };
}

/**
 * Verify a v2 payload against the key set, the line's variant + block, and the current
 * store-local date. Returns the signed discount to apply, or null when nothing should be
 * applied: no payload, a malformed payload, a malformed current date, an unknown key id,
 * a forged tag, or a signature whose expiry has passed.
 */
export function verifyDiscountSignature(
  keys: Record<string, string>,
  args: VerifyDiscountSignatureArgs
): VerifiedDiscount | null {
  if (!args.payload) {
    return null;
  }
  if (!DATE_PATTERN.test(args.currentDate)) {
    return null;
  }

  const parsed = parseDiscountSignature(args.payload);
  if (!parsed) {
    return null;
  }

  const secret = ownKey(keys, parsed.kid);
  if (!secret) {
    return null;
  }

  const expected = upsellMacHex(
    secret,
    buildDiscountSignatureMessage(
      parsed,
      upsellVariantGid(args.variantId),
      args.blockId
    )
  );
  if (!timingSafeEqualHex(expected, parsed.tag)) {
    return null;
  }

  // Day granular and store local: a signature is good through the end of `exp`.
  if (parsed.exp < args.currentDate) {
    return null;
  }

  return {
    discountType: parsed.discountType,
    discountTiers: parsed.discountTiers,
  };
}

/** Return a copy of an offer with every pre-existing signature removed. */
export function stripUpsellProductSignatures(
  products: UpsellProductItem[]
): UpsellProductItem[] {
  return products.map((product) => ({
    ...product,
    variants: product.variants.map((variant) => {
      const unsigned = { ...variant };
      delete unsigned.sig;
      return unsigned;
    }),
  }));
}
